/**
 * 取引先パターン・マスタ API サービス
 *
 * 顧客ごとの取引先名寄せ機能に関するサービスです。
 * Cloud Functions (Callable) を使用してバックエンドと通信します。
 */

import { getFunctionsInstance, httpsCallable } from "../lib/firebase";

import { EmailConfig, SlipTypeConfig } from "./csvSettingsService";

/**
 * 取引先パターン
 */
export interface PartnerPattern {
  id: string;
  pattern: string;
  occurrences: number;
  firstSeen: string;
  lastSeen: string;
  mappedTo?: string;
}

/**
 * 取引先マスタ
 */
export interface Partner {
  id: string;
  officialName: string;
  source: "ai" | "manual";
  confidence?: number;
  createdAt: string;
  updatedAt: string;
  patterns?: PartnerPattern[];
  emailConfig?: EmailConfig;
  /** 伝票種別ごとの個別設定 */
  slipTypeConfigs?: Record<string, SlipTypeConfig>;
}

/**
 * 取引先の設定状態を判定する共通ロジック
 */
export const checkPartnerStatus = (partner: Partner) => {
  const slipTypeCount = partner.slipTypeConfigs
    ? Object.keys(partner.slipTypeConfigs).length
    : 0;

  // いずれかの伝票種別にCSV設定（カラムが1つ以上）があれば設定済み
  const isCsvConfigured = partner.slipTypeConfigs
    ? Object.values(partner.slipTypeConfigs).some(
        (config) => !!config.csvSchema && config.csvSchema.columns.length > 0
      )
    : false;

  const isEmailConfigured = !!partner.emailConfig && partner.emailConfig.replyTo.length > 0;
  
  return {
    isCsvConfigured,
    isEmailConfigured,
    slipTypeCount,
    isAllConfigured: isCsvConfigured && isEmailConfigured
  };
};

/**
 * 共通のエラーハンドリング
 * Firebase の Callable Function はエラー時に HttpsError を投げる
 */
function handleError(error: unknown): never {
  console.error("PartnerService Error:", error);
  const message = error instanceof Error ? error.message : "予期せぬエラーが発生しました";
  throw new Error(message);
}

/**
 * 取引先マスタ一覧を取得
 */
export async function fetchPartners(customerId: string): Promise<Partner[]> {
  try {
    const functions = getFunctionsInstance();
    const getPartners = httpsCallable(functions, "getPartners");
    const result = await getPartners({ customerId });
    const data = result.data as { partners: Partner[] };
    return data.partners;
  } catch (error) {
    handleError(error);
  }
}

/**
 * 取引先マスタを作成
 */
export async function createPartner(
  customerId: string,
  officialName: string
): Promise<Partner> {
  try {
    const functions = getFunctionsInstance();
    const createPartnerFn = httpsCallable(functions, "createPartner");
    const result = await createPartnerFn({ customerId, name: officialName });
    const { partnerId } = result.data as { partnerId: string };

    return {
      id: partnerId,
      officialName,
      source: "manual",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  } catch (error) {
    handleError(error);
  }
}

/**
 * 取引先マスタを更新
 */
export async function updatePartner(
  customerId: string,
  partnerId: string,
  updates: Partial<Pick<Partner, "officialName" | "emailConfig" | "slipTypeConfigs">>
): Promise<void> {
  try {
    const functions = getFunctionsInstance();
    const updatePartnerFn = httpsCallable(functions, "updatePartner");
    await updatePartnerFn({ customerId, partnerId, updates });
  } catch (error) {
    handleError(error);
  }
}

/**
 * 取引先マスタを削除
 */
export async function deletePartner(
  customerId: string,
  partnerId: string
): Promise<void> {
  try {
    const functions = getFunctionsInstance();
    const deletePartnerFn = httpsCallable(functions, "deletePartner");
    await deletePartnerFn({ customerId, partnerId });
  } catch (error) {
    handleError(error);
  }
}

/**
 * 未分類パターン一覧を取得
 */
export async function fetchUnmappedPatterns(
  customerId: string
): Promise<PartnerPattern[]> {
  try {
    const functions = getFunctionsInstance();
    const getUnmappedPatterns = httpsCallable(functions, "getUnmappedPatterns");
    const result = await getUnmappedPatterns({ customerId });
    const data = result.data as { patterns: PartnerPattern[] };
    return data.patterns;
  } catch (error) {
    handleError(error);
  }
}

/**
 * パターンを取引先に紐付け
 * 
 * @param patternId - パターンのFirestore Document ID
 * @param partnerId - 紐付け先の取引先ID
 */
export async function mapPatternToPartner(
  customerId: string,
  patternId: string,
  partnerId: string
): Promise<void> {
  try {
    const functions = getFunctionsInstance();
    const mapPattern = httpsCallable(functions, "mapPattern");
    // バックエンドAPI は `pattern` キーで patternId を受け取る
    await mapPattern({ customerId, pattern: patternId, partnerId });
  } catch (error) {
    handleError(error);
  }
}

/**
 * パターンを手動で追加し、取引先に紐付ける
 */
export async function addManualPattern(
    customerId: string,
    pattern: string,
    partnerId: string
): Promise<void> {
    try {
        const functions = getFunctionsInstance();
        const addManualPatternFn = httpsCallable(functions, "addManualPattern");
        await addManualPatternFn({ customerId, pattern, partnerId });
    } catch (error) {
        handleError(error);
    }
}

/**
 * パターンの紐付けを解除
 */
export async function unmapPattern(
    customerId: string,
    patternId: string
): Promise<void> {
    try {
        const functions = getFunctionsInstance();
        const unmapPatternFn = httpsCallable(functions, "unmapPattern");
        await unmapPatternFn({ customerId, patternId });
    } catch (error) {
        handleError(error);
    }
}

